import {
  Clock,
  sRGBEncoding,
  WebGLRenderer
} from 'three';

window.WIDTH = window.innerWidth;
window.HEIGHT = window.innerHeight;

window.Time = {
  delta: 0,
  elapsed: 0
};

export class RenderLoop {
  constructor(options) {
    options = options !== undefined ? options : {};

    this._options = options;
    this.clock = new Clock();
    this.paused = false;
    this.cameras = [];
    this.scene = null;
    this._drawCalls = 0;
    this._onUpdateCallback = options.onUpdate;
    this._onRenderCallback = options.onRender;
    this._onResizeCallback = options.onResize;

    this.renderer = new WebGLRenderer({
      alpha: options.alpha,
      antialias: options.antialias,
      canvas: options.canvas || document.querySelector('canvas'),
      preserveDrawingBuffer: options.preserveDrawingBuffer !== undefined ? options.preserveDrawingBuffer : undefined
    });

    this.renderer.outputEncoding = sRGBEncoding;

    this.config = {
      profiling: options.profiling !== undefined ? options.profiling : false,
      logDrawCalls: options.logDrawCalls !== undefined ? options.logDrawCalls : false
    };

    let pixelRatio = window.devicePixelRatio;
    if (options && options.maxPixelRatio && pixelRatio > options.maxPixelRatio) {
      pixelRatio = options.maxPixelRatio;
    }

    this.renderer.setPixelRatio(pixelRatio);

    this.setSize(options.width || window.innerWidth, options.height || window.innerHeight);

    if (options.autoClear !== undefined) {
      this.renderer.autoClear = options.autoClear;
    }

    if (options.clearColor !== undefined) {
      this.renderer.setClearColor(options.clearColor);
    }

    window.addEventListener('resize', this.onWindowResize.bind(this), false);

    this.renderer.domElement.addEventListener('mousemove', function(e) {
      window.mouseX = ((e.pageX / window.WIDTH) * 2) - 1;
      window.mouseY = 1 - ((e.pageY / window.HEIGHT) * 2);
    });

    handleVisibilityChanges(this);

    Object.defineProperty(this, 'domElement', {
      get() {
        return this.renderer.domElement;
      }
    });
  }

  registerCamera(camera) {
    if (camera) {
      this.cameras.push(camera);
    } else {
      console.error("RenderLoop: couldn't register camera.");
    }
  }

  render() {
    if (this._onRenderCallback) {
      this._onRenderCallback();
    }
  }

  renderScene(scene, camera) {
    this.renderer.render(scene, camera);

    if (this.config.logDrawCalls) {
      this._drawCalls += this.renderer.info.render.calls;
    }
  }

  update() {
    this.cameras.forEach(function(camera) {
      camera.updateMatrixWorld(true);
      camera.matrixWorldInverse.copy(camera.matrixWorld).invert();
    }, this);

    if (this._onUpdateCallback) {
      this._onUpdateCallback();
    }
  }

  doUpdate() {
    window.Time.delta = this.clock.getDelta();
    window.Time.elapsed = this.clock.getElapsedTime();

    if (!this.paused) {
      requestAnimationFrame(this.doUpdate.bind(this));

      if (this.config.logDrawCalls) {
        this._drawCalls = 0;
      }
      if (this.config.profiling) console.time("update");
      this.update();
      if (this.config.profiling) console.timeEnd("update");
      this.render();
      if (this.config.logDrawCalls) {
        this.logDrawCalls(this._drawCalls);
      }
    }
  }

  start() {
    if (!this.started) {
      this.doUpdate();

      this.started = true;
    }
  }

  stop() {
    if (this.started) {
      this.clock.stop();
      this.started = false;
      this.paused = false;
    }
  }

  pause() {
    if (!this.paused) {
      this.clock.stop();
      this.paused = true;
    }
  }

  resume() {
    if (this.paused) {
      this.clock.start();
      this.paused = false;
      if (this.started) {
        this.doUpdate();
      }
    }
  }

  onLeaveTab() {
    if (!this.paused) {
      this.pause();
      this.shouldResume = true;
    }
  }

  onFocusTab() {
    if (this.shouldResume) {
      this.resume();
      this.shouldResume = false;
    }
  }

  setAspectRatio(aspect) {
    this.cameras.forEach(function(camera) {
      camera.aspect = aspect;
      camera.updateProjectionMatrix();
    }, this);
  }

  refreshAspectRatio() {
    var size = this.renderer.getSize();
    this.setAspectRatio(size.width / size.height);
  }

  setSize(width, height) {
    this.setAspectRatio(width / height);
    this.renderer.setSize(width, height);
  }

  logDrawCalls(count) {
    // Meant to overriden in child classes (for example, in order to update a DOM node)
    console.log("Draw calls:", count);
  }

  onWindowResize() {
    var width = window.WIDTH = window.innerWidth;
    var height = window.HEIGHT = window.innerHeight;

    this.setSize(width, height);

    if (this._onResizeCallback) {
      this._onResizeCallback(width, height);
    }
  }
}

function handleVisibilityChanges(app) {
  var hidden, visibilityChange;
  if (typeof document.hidden !== "undefined") {
    hidden = "hidden";
    visibilityChange = "visibilitychange";
  } else if (typeof document.mozHidden !== "undefined") {
    hidden = "mozHidden";
    visibilityChange = "mozvisibilitychange";
  } else if (typeof document.msHidden !== "undefined") {
    hidden = "msHidden";
    visibilityChange = "msvisibilitychange";
  } else if (typeof document.webkitHidden !== "undefined") {
    hidden = "webkitHidden";
    visibilityChange = "webkitvisibilitychange";
  }

  // Pause/resume app when switching tabs
  if (typeof document.addEventListener !== "undefined") {
    document.addEventListener(visibilityChange, function handleVisibilityChange() {
      if (document[hidden]) {
        app.onLeaveTab();
      } else {
        setTimeout(app.onFocusTab.bind(app), 50);
      }
    }, false);
  }
}
